import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Trophy, Star, Target, Gift, Crown, Zap, TrendingUp, Users, Award, Flame } from 'lucide-react';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  progress: number;
  total: number;
  completed: boolean;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  reward: string;
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  progress: number;
  total: number;
  timeLeft: string;
  reward: string;
  type: 'daily' | 'weekly' | 'monthly';
}

export function GamificationDashboard() {
  const [userLevel] = useState(42);
  const [userXP] = useState(8750);
  const [nextLevelXP] = useState(10000);
  const [streak] = useState(15);
  const [totalPoints] = useState(125400);

  const achievements: Achievement[] = [
    {
      id: '1',
      title: 'First Million',
      description: 'Reach $1M portfolio value',
      icon: <Crown className="h-6 w-6" />,
      progress: 750000,
      total: 1000000,
      completed: false,
      rarity: 'legendary',
      reward: '500 NDH'
    },
    {
      id: '2',
      title: 'Trade Master',
      description: 'Complete 1000 successful trades',
      icon: <TrendingUp className="h-6 w-6" />,
      progress: 847,
      total: 1000,
      completed: false,
      rarity: 'epic',
      reward: '200 NDH'
    },
    {
      id: '3',
      title: 'Social Influencer',
      description: 'Get 500 copy traders',
      icon: <Users className="h-6 w-6" />,
      progress: 342,
      total: 500,
      completed: false,
      rarity: 'rare',
      reward: '100 NDH'
    },
    {
      id: '4',
      title: 'Streak Legend',
      description: 'Maintain 30-day trading streak',
      icon: <Flame className="h-6 w-6" />,
      progress: 15,
      total: 30,
      completed: false,
      rarity: 'epic',
      reward: 'Exclusive NFT'
    }
  ];

  const dailyChallenges: Challenge[] = [
    {
      id: '1',
      title: 'Morning Trader',
      description: 'Complete 3 trades before 12 PM',
      progress: 1,
      total: 3,
      timeLeft: '4h 32m',
      reward: '50 XP + 10 NDH',
      type: 'daily'
    },
    {
      id: '2',
      title: 'Portfolio Diversifier',
      description: 'Hold 5 different tokens',
      progress: 3,
      total: 5,
      timeLeft: '4h 32m',
      reward: '100 XP + 25 NDH',
      type: 'daily'
    },
    {
      id: '3',
      title: 'Staking Enthusiast',
      description: 'Stake in 2 different pools',
      progress: 2,
      total: 2,
      timeLeft: '4h 32m',
      reward: '150 XP + 50 NDH',
      type: 'daily'
    }
  ];

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'text-yellow-500 border-yellow-500';
      case 'epic': return 'text-purple-500 border-purple-500';
      case 'rare': return 'text-blue-500 border-blue-500';
      default: return 'text-gray-500 border-gray-500';
    }
  };

  const getRarityBg = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'bg-gradient-to-r from-yellow-400 to-orange-500';
      case 'epic': return 'bg-gradient-to-r from-purple-400 to-pink-500';
      case 'rare': return 'bg-gradient-to-r from-blue-400 to-cyan-500';
      default: return 'bg-gradient-to-r from-gray-400 to-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Player Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Level</p>
                <p className="text-2xl font-bold">{userLevel}</p>
              </div>
              <Crown className="h-8 w-8 opacity-90" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Points</p>
                <p className="text-2xl font-bold">{totalPoints.toLocaleString()}</p>
              </div>
              <Star className="h-8 w-8 opacity-90" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Streak</p>
                <p className="text-2xl font-bold">{streak} days</p>
              </div>
              <Flame className="h-8 w-8 opacity-90" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">XP Progress</p>
                <p className="text-lg font-bold">{userXP}/{nextLevelXP}</p>
              </div>
              <Zap className="h-8 w-8 opacity-90" />
            </div>
            <Progress value={(userXP / nextLevelXP) * 100} className="mt-2 h-2" />
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="challenges" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="challenges">Daily Challenges</TabsTrigger>
          <TabsTrigger value="achievements">Achievements</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
        </TabsList>

        <TabsContent value="challenges" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Daily Challenges
              </CardTitle>
              <CardDescription>
                Complete daily challenges to earn XP and NDH rewards
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {dailyChallenges.map((challenge) => (
                <div key={challenge.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{challenge.title}</h4>
                      <p className="text-sm text-muted-foreground">{challenge.description}</p>
                    </div>
                    <Badge variant={challenge.progress >= challenge.total ? "default" : "secondary"}>
                      {challenge.timeLeft}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress: {challenge.progress}/{challenge.total}</span>
                      <span>{challenge.reward}</span>
                    </div>
                    <Progress value={(challenge.progress / challenge.total) * 100} className="h-2" />
                  </div>

                  {challenge.progress >= challenge.total && (
                    <Button size="sm" className="w-full">
                      <Gift className="h-4 w-4 mr-2" />
                      Claim Reward
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="achievements" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Achievements
              </CardTitle>
              <CardDescription>
                Unlock achievements to earn exclusive rewards and boost your reputation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {achievements.map((achievement) => (
                <div key={achievement.id} className={`border-2 rounded-lg p-4 ${getRarityColor(achievement.rarity)}`}>
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${getRarityBg(achievement.rarity)}`}>
                      {achievement.icon}
                    </div>
                    
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">{achievement.title}</h4>
                        <Badge className={getRarityColor(achievement.rarity)}>
                          {achievement.rarity}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground">{achievement.description}</p>
                      
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span>{achievement.progress.toLocaleString()}/{achievement.total.toLocaleString()}</span>
                        </div>
                        <Progress value={(achievement.progress / achievement.total) * 100} className="h-2" />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Reward: {achievement.reward}</span>
                        {achievement.completed && (
                          <Badge variant="default">
                            <Award className="h-3 w-3 mr-1" />
                            Completed
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaderboard" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Global Leaderboard
              </CardTitle>
              <CardDescription>
                Compete with other NDH traders for the top spot
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((rank) => (
                  <div key={rank} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : rank === 3 ? 'bg-orange-500' : 'bg-gray-200'
                      } text-white font-bold`}>
                        {rank}
                      </div>
                      <div>
                        <p className="font-medium">Trader_{rank}234</p>
                        <p className="text-sm text-muted-foreground">Level {45 - rank}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{(150000 - rank * 10000).toLocaleString()} pts</p>
                      <p className="text-sm text-green-500">+{500 - rank * 50} today</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}